import type { KeyboardEvent } from 'react';
import {
	KeyboardEventHandler,
	MouseEvent,
	MouseEventHandler,
	ReactElement,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

export type Tab = {
	id: string;
	title: string;
	content: ReactElement;
};

type SubnavWithSidebarTabsProps = {
	tabs: Tab[];
	links?: ReactElement;
	defaultTab?: number;
	onTabInView?: (tabIndex: number) => void;
};

const useSafeLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const tabContainerStyles = css({
	display: 'grid',
	gridTemplateAreas: `
        "tablist tabpanel"
    `,
	gridTemplateColumns: 'max-content',
	backgroundColor: wacToken('color.neutral.light100'),
	fontFamily: 'Charlie Display',
});

const tablistWrapperStyle = css({
	gridArea: 'tablist',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	backgroundColor: wacToken('color.neutral.light100'),
	paddingBlockStart: '40px',
	paddingBlockEnd: '40px',
	height: 'max-content',
	width: '224px',
	gap: token('space.1000'),
});

const tablistStyles = css({
	display: 'flex',
	flexDirection: 'column',
	color: wacToken('color.primary.black'),
	paddingInlineStart: token('space.500'),
});

const tabpanelStyles = css({
	gridArea: 'tabpanel',
	backgroundColor: wacToken('color.neutral.light0'),
	position: 'relative',
});

function isValidTab(tabs: Tab[], index: number): boolean {
	return Array.isArray(tabs) && index > -1 && index < tabs.length;
}

interface UseEventHandlerParams {
	next?: (start: number) => number | boolean;
	previous?: (start: number) => number | boolean;
	select?: (item: number) => void | boolean;
	deleteFocus?: (panel: number) => void | boolean;
}

type EventsHandlers = {
	createOnClick: (index: number) => MouseEventHandler<unknown>;
	createOnKeyDown: (index: number) => KeyboardEventHandler<unknown>;
};

function noop(): boolean {
	return false;
}

function useEventHandler({
	previous = noop,
	next = noop,
	select = noop,
	deleteFocus = noop,
}: UseEventHandlerParams): EventsHandlers {
	const keyMap = {
		ArrowUp: (start: number) => previous(start),
		ArrowDown: (start: number) => next(start),
		Delete: (panel: number) => deleteFocus(panel),
	};

	return {
		createOnClick: (index: number) => (_evt: MouseEvent) => select(index),
		createOnKeyDown: (index: number) => {
			return <E extends HTMLElement>(evt: KeyboardEvent<E>) => {
				const moveAction = keyMap[evt.key as keyof typeof keyMap];

				if (!moveAction) {
					return;
				}

				const next = moveAction(index);
				if (next === false) {
					return;
				}

				evt.preventDefault();
				if (typeof next !== 'undefined' && typeof next !== 'boolean') {
					select(next);
				}
			};
		},
	};
}

type TabProps = {
	isActive: boolean;
	isFocused: boolean;
	tab: Tab;
	onClick: MouseEventHandler<HTMLDivElement>;
	onKeyDown: KeyboardEventHandler<HTMLDivElement>;
};

const tabStyle = css({
	backgroundColor: 'inherit',
	color: wacToken('color.primary.black'),
	display: 'flex',
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.400'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	fontSize: '14px',
	fontWeight: 700,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[aria-selected="true"]': {
		backgroundColor: wacToken('color.neutral.light0'),
	},
	cursor: 'pointer',
	borderRadius: '0.5rem 0 0 0.5rem',
	width: '184px',
});

const activeTabStyle = css({
	backgroundColor: wacToken('color.neutral.light0'),
	color: wacToken('color.primary.blue'),
});

const Tab = ({ tab, onClick, onKeyDown, isActive, isFocused }: TabProps) => {
	const { id, title } = tab;
	const tabRef = useRef<HTMLDivElement>(null);

	useSafeLayoutEffect(() => {
		if (isFocused && tabRef.current) {
			tabRef.current.focus();
		}
	}, [isFocused]);

	return (
		<div
			id={`tab-${id}`}
			ref={tabRef}
			role="tab"
			onClick={onClick}
			onKeyDown={onKeyDown}
			aria-selected={isActive}
			aria-controls={`panel-${id}`}
			tabIndex={isActive ? 0 : -1}
			css={[tabStyle, isActive && activeTabStyle]}
		>
			<span>{title}</span>
		</div>
	);
};

// TODO we should refactor out the Tab primitive into WPL or use an
// existing ARIA-friendly primitive library
export const SubnavWithSidebarTabs = ({
	tabs,
	links,
	defaultTab = 0,
	onTabInView,
}: SubnavWithSidebarTabsProps) => {
	const intl = useIntl();
	const containerRef = useRef<HTMLDivElement>(null);

	const [activeTabIndex, setActiveTabIndex] = useState(() => {
		if (isValidTab(tabs, defaultTab)) {
			return defaultTab;
		}

		return 0;
	});
	const [focusTab, setFocusTab] = useState<number>();

	const selectFocusableTab = (tab: number): void => {
		let nextTab = 0;

		if (tab < tabs.length) {
			nextTab = tab;
		} else if (tab - 2 >= 0) {
			nextTab = tab - 2;
		}

		setActiveTabIndex(nextTab);
		setFocusTab(nextTab);
	};

	const { createOnClick, createOnKeyDown } = useEventHandler({
		previous: (start: number) => {
			const nextIndex = start - 1;

			return isValidTab(tabs, nextIndex) ? nextIndex : tabs.length - 1;
		},
		next: (start: number) => {
			const nextIndex = start + 1;

			return isValidTab(tabs, nextIndex) ? nextIndex : 0;
		},
		select: selectFocusableTab,
	});

	// Observes the tab container to determine when a tab is in view
	useLayoutEffect(() => {
		// Check if IntersectionObserver is supported
		if (typeof IntersectionObserver === 'undefined') {
			return;
		}

		if (!onTabInView) {
			return;
		}

		const currentContainer = containerRef.current;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					onTabInView(activeTabIndex);
				}
			},
			{
				root: null,
				rootMargin: '0px',
				threshold: 0.1,
			},
		);

		if (currentContainer) {
			observer.observe(currentContainer);
		}

		return () => {
			if (currentContainer) {
				observer.unobserve(currentContainer);
			}
		};
	}, [activeTabIndex, onTabInView, tabs]);

	// Handle Backspace key at document level to catch events from all children
	useEffect(() => {
		const handleDocumentKeyDown = (event: Event) => {
			if (!(event instanceof KeyboardEvent)) {
				return;
			}
			const keyboardEvent = event;
			if (
				keyboardEvent.key === 'Backspace' &&
				containerRef.current?.contains(event.target as Node)
			) {
				const nextTabIndex =
					activeTabIndex == tabs.length - 1 ? activeTabIndex - 1 : activeTabIndex + 1;
				setActiveTabIndex(nextTabIndex);
				setFocusTab(nextTabIndex);
				keyboardEvent.preventDefault();
				keyboardEvent.stopPropagation();
			}
		};

		document.addEventListener('keydown', handleDocumentKeyDown, true);
		return () => {
			document.removeEventListener('keydown', handleDocumentKeyDown, true);
		};
	}, [activeTabIndex, tabs.length]);

	return (
		<div ref={containerRef} css={tabContainerStyles}>
			<div css={tablistWrapperStyle}>
				<div
					role="tablist"
					aria-label={intl.formatMessage({
						id: 'globalnav.v2.subnav.tabs.aria-label-tablist',
						defaultMessage: 'Products by user category',
					})}
					aria-orientation="vertical"
					css={tablistStyles}
				>
					{tabs.map((tab, index) => {
						const isActive = index === activeTabIndex;
						const isFocused = index === focusTab;

						return (
							<Tab
								key={`key-${tab.id}`}
								isActive={isActive}
								isFocused={isFocused}
								tab={tab}
								onClick={createOnClick(index)}
								onKeyDown={createOnKeyDown(activeTabIndex)}
							/>
						);
					})}
				</div>
				{links ?? null}
			</div>
			<div css={tabpanelStyles}>
				{tabs.map((tab, index) => {
					return (
						<div
							key={index}
							id={`panel-${tab.id}`}
							role="tabpanel"
							aria-labelledby={`tab-${tab.id}`}
							tabIndex={activeTabIndex === index ? -1 : undefined}
							aria-hidden={activeTabIndex !== index}
							style={{
								position: activeTabIndex == index ? 'static' : 'absolute',
								insetBlockStart: '0',
								height: '100%',
								visibility: activeTabIndex == index ? 'visible' : 'hidden',
							}}
						>
							{tab.content}
						</div>
					);
				})}
			</div>
		</div>
	);
};
